import i18next from "i18next";
import React, { useEffect } from "react";
import { isBrowser } from "react-device-detect";
import { useTranslation } from "react-i18next";

import { Button, Spacing, Stack } from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { HealthGoal, ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import { Partner } from "@/models";
import { isB2CPartner } from "@/utils/partners";

import {
  BaseQuestion,
  ImageBulletListCard,
  ItemDefinition as BulletItemDefinition,
} from "../core";

const QUESTION_ID = "programOverview";

declare module "@/contexts" {
  interface SurveyAnswers {
    programOverview?: string;
  }
}

type ProgramOverviewConfig = {
  buttonText: string;
  program: ProgramOffering | null;
  questionText: string;
  listItems: BulletItemDefinition[];
};

const getProgramMap: (
  partnerInfo: Partner | null,
) => Record<HealthGoal, ProgramOverviewConfig> = (partnerInfo) => ({
  [HealthGoal.REDUCING_STRESS_ANXIOUS_FEELINGS]: {
    buttonText: i18next.t(
      "programOverview.ctaText.reducingStressAnxiousFeelings",
    ),
    program: "MOOD",
    questionText: i18next.t(
      "programOverview.questionText.reducingStressAnxiousFeelings",
    ),
    listItems: [
      {
        text: i18next.t(
          "programOverview.valueProps.item1.reducingStressAnxiousFeelings",
        ),
        image: "/assets/img/illustration-lightbulb.svg",
      },
      {
        text: i18next.t(
          "programOverview.valueProps.item2.reducingStressAnxiousFeelings",
        ),
        image: "/assets/img/illustration-brain.svg",
      },
    ],
  },
  [HealthGoal.LOSING_WEIGHT]: {
    buttonText: i18next.t("programOverview.ctaText.losingWeight"),
    program: null,
    questionText: i18next.t("programOverview.questionText.losingWeight"),
    listItems: [
      {
        text: i18next.t("programOverview.valueProps.item1.losingWeight", {
          context:
            isB2CPartner(partnerInfo) || partnerInfo?.configs?.HIDE_COST
              ? "notFree"
              : "free",
        }),
        image: "/assets/img/illustration-weight.svg",
      },
      {
        text: i18next.t("programOverview.valueProps.item2.losingWeight"),
        image: "/assets/img/illustration-food-logging.svg",
      },
    ],
  },
});

const ProgramOverview: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const {
    eligibilityInfo: { eligiblePrograms = [] } = {},
    partnerInfo,
    setSelectedProgram,
    setSurveyAnswers,
  } = useAppContext();
  const { t } = useTranslation();

  const { healthGoal } = surveyAnswers;

  const PROGRAM_MAP = getProgramMap(partnerInfo);

  let assignedHealthGoal = healthGoal || HealthGoal.LOSING_WEIGHT;

  if (!healthGoal && eligiblePrograms.includes("MOOD")) {
    assignedHealthGoal = HealthGoal.REDUCING_STRESS_ANXIOUS_FEELINGS;
  }

  const { buttonText, program, questionText, listItems } =
    PROGRAM_MAP[assignedHealthGoal];

  useEffect(() => {
    setSelectedProgram(program);
  }, [program]);

  return (
    <BaseQuestion
      onClickBack={() => {
        // Don't persist the selection if navigating before this question
        setSurveyAnswers({ ...surveyAnswers, [QUESTION_ID]: undefined });
        onClickBack();
      }}
      questionId={QUESTION_ID}
      questionText={questionText}
    >
      <ImageBulletListCard
        items={listItems}
        headingText={t("programOverview.heading")}
      />
      <Stack spacing={isBrowser ? Spacing[8] : Spacing[4]}>
        <Button
          autoFocus
          colorScheme="primary"
          onClick={() => {
            onClickNext(assignedHealthGoal);
          }}
          size="xl"
          type="submit"
          _focus={{
            boxShadow: "none",
          }}
        >
          {buttonText}
        </Button>
      </Stack>
    </BaseQuestion>
  );
};

export const Q_PROGRAM_OVERVIEW: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: () => true,
  component: ProgramOverview,
};
